import React, { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"

import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import {
  EFeatureFlags,
  EVENT_CLASS,
  EVENT_STATUS,
  EventDocData,
  EventStatusType,
} from "@letshang/constants"
import { get, random } from "lodash-es"
import moment from "moment"
import { ROUTES } from "src/App"

import { useProcessFeatureFlags } from "@settingsAlias/featureFlags"

import {
  DownloadLinkMap,
  onOpenDownloadLink,
  useDeviceDetection,
} from "@fuse/utils/DeviceDection"

import EventsBuddiesAvatar from "@fuse/pages/landingPage/components/EventBuddiesAvatar"

import NoEventImg from "@fuse/assets/images/img-no-events.png"

export const randomImageUri = `https://picsum.photos/1018/536?random=${random(0, 500)}`

interface IEventCard {
  event: EventDocData
}

const EventCard: React.FC<IEventCard> = ({ event }) => {
  const navigate = useNavigate()
  const device = useDeviceDetection()

  const imageRef = useRef<HTMLImageElement>(null)
  const downloadLink = DownloadLinkMap[device]
  const eventId = get(event, "eventId", "")
  const minTicketPrice = get(event, "minTicketPrice", 0)
  const isEmptySection = get(event, "isEmptySection", false)
  const isBuddiesEnabled =
    useProcessFeatureFlags(EFeatureFlags.BUDDIES_SECTION) || false

  const externalEventsBuddies = get(event, "guestAvatars", [])
  const mutualBuddies = get(event, "muturalBuddies", externalEventsBuddies)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (imageRef.current?.complete) {
      setLoading(false)
    }
  }, [])

  // console.log("externalEventsBuddies ::>>>>", externalEventsBuddies)

  const getLabel = () => {
    if (event?.eventStatus === EVENT_STATUS.OPEN) {
      switch (get(event, "myJoinStatus")) {
        case EventStatusType.INITIATOR:
          return "🎉 Hosting"
        case EventStatusType.CO_HOST:
          return "🎉 Co-hosting"
        case EventStatusType.ACCEPTED:
          return "✅  Joined"
        case EventStatusType.DECLINED:
          return "🚫  Declined"
        case EventStatusType.UNDER_REVIEW:
          return "🕒  Waiting for approval"
        default: {
          if (event.eventClass === EVENT_CLASS.NON_TICKETED_EVENT) return "Free"
          else return `$${minTicketPrice}`
        }
      }
    } else if (event?.eventStatus === EVENT_STATUS.CLOSE) {
      return "❌  Cancelled"
    }
  }

  const formatEventTime = (eventDate?: string, eventStartTime?: string) => {
    const isToday = moment(eventDate).isSame(moment(), "day")
    const formattedDate = isToday ? "Today" : moment(eventDate).format("D MMM")
    const formattedTime = moment(eventStartTime).format("h:mm A")
    return `${formattedDate} • ${formattedTime}`
  }

  const handleOnEvent = () => {
    if (eventId) navigate(ROUTES.EVENTS.replace(":eventId", eventId))
  }

  if (isEmptySection) {
    return (
      <div
        className="flex flex-col h-80 w-full rounded-2xl overflow-hidden relative cursor-pointer"
        onClick={() => navigate(ROUTES.CREATE_EVENT_V5)}
      >
        {loading && (
          <div className="absolute inset-0 z-30 bg-white/20 rounded-2xl animate-pulse" />
        )}
        <img
          ref={imageRef}
          src={randomImageUri}
          alt="No events"
          className="w-full h-full absolute object-cover"
          onLoad={() => setLoading(false)}
        />

        {!loading && (
          <BlurWrapper
            overlayColorDefault="dark"
            className="flex flex-col h-full w-full p-8 z-10 items-center justify-center"
          >
            <BlurWrapper
              overlayOpacityDefault="5"
              className="h-12 w-12 rounded-full -z-10 flex items-center justify-center"
            >
              <img src={NoEventImg} className="h-6 w-6" />
            </BlurWrapper>
            <p className="text-lg text-white font-medium text-center z-10">
              No events
            </p>
            <p className="text-sm text-white/50 text-center z-10">
              Create your own event and start hanging out with friends!
            </p>
          </BlurWrapper>
        )}
      </div>
    )
  } else {
    return !eventId ? null : (
      <div
        className="flex flex-col h-80 w-full rounded-2xl overflow-hidden relative cursor-pointer"
        onClick={handleOnEvent}
      >
        {loading && (
          <div className="absolute inset-0 z-30 bg-white/20 rounded-2xl animate-pulse" />
        )}
        <img
          ref={imageRef}
          src={get(event, "flyerImage", "")}
          className="w-full h-full object-cover absolute"
          onLoad={() => setLoading(false)}
        />
        {!loading && (
          <>
            <div className="flex h-full relative">
              <>
                {event?.eventStatus &&
                  event?.eventStatus !== EVENT_STATUS.DRAFT && (
                    <BlurWrapper
                      overlayOpacityDefault="5"
                      overlayColorDefault="dark"
                      className="flex p-2 h-8 absolute top-3 left-3 items-center justify-center rounded-lg"
                    >
                      <p className="text-sm text-white text-center">
                        {getLabel()}
                      </p>
                    </BlurWrapper>
                  )}

                {isBuddiesEnabled && (
                  <EventsBuddiesAvatar mutualBuddies={mutualBuddies} />
                )}
              </>

              {/* <div className="absolute bottom-0 right-3 h-8 w-80 bg-red-300"></div> */}
              <BlurWrapper
                overlayOpacityDefault="5"
                overlayColorDefault="dark"
                className="flex p-2 h-8 absolute bottom-3 right-3 items-center justify-center rounded-lg"
              >
                <p className="text-sm text-white text-center">
                  {event?.eventStatus === EVENT_STATUS.DRAFT ||
                  !event?.eventStatus
                    ? "✏️  Draft"
                    : `🗓️ ${formatEventTime(event?.timing?.eventDate, event?.timing?.eventStartTime)}`}
                </p>
              </BlurWrapper>
            </div>
            <BlurWrapper
              overlayOpacityDefault="5"
              overlayColorDefault="dark"
              className="flex w-full items-start p-3 bottom-0 flex-col"
            >
              <p className="text-white">{event?.eventName}</p>
              <p className="text-white/35 line-clamp-1">
                {event?.location?.eventLocation}
              </p>
            </BlurWrapper>
          </>
        )}
      </div>
    )
  }
}

export default EventCard
